import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateHealthWorkerModal from './CreateHealthWorkerModal';
import CreateAdminModal from './CreateAdminModal';
import BulkUploadModal from './bulkupload';
import ExportModal from './exportmodal';
import './UserManagement.css';
import { getLearners, getAdmins, suspendAdmin, suspendLearner, deleteAdmin, deleteLearner, deleteUser, createUser, createAdmin, bulkUploadUsers, suspendUser } from '../api';
import course1 from '../assets/1.jpg';
import course2 from '../assets/1.jpg';
import course3 from '../assets/1.jpg';

const ADMIN_FILTER_OPTIONS = [
  { id: 'all', label: 'All admins' },
  { id: 'admin', label: 'Admins' },
  { id: 'super_admin', label: 'Super admins' },
];

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('health-workers');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showCreateHealthWorkerModal, setShowCreateHealthWorkerModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadType, setBulkUploadType] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [allHealthWorkers, setAllHealthWorkers] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Backend pagination for health workers
  const [hwPagination, setHwPagination] = useState({ currentPage: 1, lastPage: 1, perPage: 50, total: 0 });
  // Frontend pagination for admins (fetched all at once)
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [adminUsersPerPage] = useState(25);
  const [adminRoleFilter, setAdminRoleFilter] = useState('all');
  const [adminViewMode, setAdminViewMode] = useState('table');
  const [hwDateFrom, setHwDateFrom] = useState('');
  const [hwDateTo, setHwDateTo] = useState('');
  const [adminDateFrom, setAdminDateFrom] = useState('');
  const [adminDateTo, setAdminDateTo] = useState('');
  const [adminStats, setAdminStats] = useState({
    total: 0,
    active: 0,
    superAdmins: 0,
    reachable: 0,
    recent: null,
  });
  const [refreshingAdmins, setRefreshingAdmins] = useState(false);
  const [adminDataLoaded, setAdminDataLoaded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null, userName: '', userType: '' });
  const [deleting, setDeleting] = useState(false);
  const [activating, setActivating] = useState({ userId: null, active: false });
  const navigate = useNavigate();

  const calculateAge = (dobString) => {
    if (!dobString) return 'N/A';
    const dob = new Date(dobString);
    if (isNaN(dob)) return 'N/A';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const resolveFacilityName = (entity = {}) => {
    if (entity.facility && typeof entity.facility === 'object') {
      return (
        entity.facility.name ||
        entity.facility.title ||
        entity.facility.facility_name ||
        'N/A'
      );
    }
    if (typeof entity.facility === 'string' && entity.facility.trim()) {
      return entity.facility.trim();
    }
    if (entity.facility_name && entity.facility_name.trim() && entity.facility_name !== 'N/A') {
      return entity.facility_name.trim();
    }
    if (entity.facility_id) {
      return `Facility #${entity.facility_id}`;
    }
    return 'N/A';
  };

  const resolveLgaName = (entity = {}) => {
    if (entity.lga && typeof entity.lga === 'object') {
      return entity.lga.name || entity.lga.title || entity.lga.lga_name || 'N/A';
    }
    if (typeof entity.lga === 'string' && entity.lga.trim()) {
      return entity.lga.trim();
    }
    if (entity.lga_id && entity.state?.lgas && Array.isArray(entity.state.lgas)) {
      const found = entity.state.lgas.find((lga) => lga.id === entity.lga_id);
      if (found?.name) return found.name;
    }
    if (entity.lga_name && entity.lga_name.trim() && entity.lga_name !== 'N/A') {
      return entity.lga_name.trim();
    }
    if (entity.lga_id) {
      return `LGA #${entity.lga_id}`;
    }
    return 'N/A';
  };

  const formatHealthWorker = (hw) => {
    let jobRole = 'N/A';
    if (hw.cadre) {
      if (typeof hw.cadre === 'object' && hw.cadre !== null) {
        jobRole = hw.cadre.name || hw.cadre.title || 'N/A';
      } else if (typeof hw.cadre === 'string' && hw.cadre.trim() !== '') {
        jobRole = hw.cadre;
      }
    }
    if (jobRole === 'N/A' && hw.job_title) {
      if (typeof hw.job_title === 'object' && hw.job_title !== null) {
        jobRole = hw.job_title.title || hw.job_title.name || 'N/A';
      } else if (typeof hw.job_title === 'string' && hw.job_title.trim() !== '') {
        jobRole = hw.job_title;
      }
    }

    // Determine status from backend data
    let status = 'Active';
    if (hw.is_active === 0 || hw.is_active === false) {
      status = 'Inactive/Suspended';
    } else if (hw.is_suspended === 1 || hw.is_suspended === true) {
      status = 'Inactive/Suspended';
    } else if (hw.status) {
      const statusLower = hw.status.toLowerCase();
      if (statusLower === 'suspended' || statusLower === 'inactive') {
        status = 'Inactive/Suspended';
      } else {
        status = hw.status.charAt(0).toUpperCase() + hw.status.slice(1).toLowerCase();
      }
    }

    return {
      id: hw.id,
      name: hw.full_name || `${hw.firstname || ''} ${hw.lastname || ''}`.trim() || 'Unknown',
      dob: hw.dob || null,
      age: calculateAge(hw.dob),
      facility: resolveFacilityName(hw),
      lga: resolveLgaName(hw),
      jobRole,
      image: hw.image || course1,
      status: status,
      email: hw.email || '',
      phone: hw.phone_number || '',
    };
  };

  const formatAdmin = (admin) => {
    let jobRole = 'N/A';
    if (admin.jobtitle) {
      if (typeof admin.jobtitle === 'object' && admin.jobtitle !== null) {
        jobRole = admin.jobtitle.name || admin.jobtitle.title || 'N/A';
      } else if (typeof admin.jobtitle === 'string' && admin.jobtitle.trim() !== '') {
        jobRole = admin.jobtitle;
      }
    } else if (admin.job_title) {
      if (typeof admin.job_title === 'object' && admin.job_title !== null) {
        jobRole = admin.job_title.name || admin.job_title.title || 'N/A';
      } else if (typeof admin.job_title === 'string' && admin.job_title.trim() !== '') {
        jobRole = admin.job_title;
      }
    } else if (admin.job_title_id) {
      jobRole = `Job Title #${admin.job_title_id}`;
    }

    const roleType = admin.role || (admin.is_super_admin === 1 ? 'super_admin' : 'admin');
    const createdAtRaw = admin.created_at || admin.createdAt || null;
    const lastActiveRaw = admin.last_login_at || admin.last_login || admin.updated_at || null;

    // Determine status from backend data
    let status = 'Active';
    if (admin.is_active === 0 || admin.is_active === false) {
      status = 'Inactive/Suspended';
    } else if (admin.is_suspended === 1 || admin.is_suspended === true) {
      status = 'Inactive/Suspended';
    } else if (admin.status) {
      const statusLower = admin.status.toLowerCase();
      if (statusLower === 'suspended' || statusLower === 'inactive') {
        status = 'Inactive/Suspended';
      } else {
        status = admin.status.charAt(0).toUpperCase() + admin.status.slice(1).toLowerCase();
      }
    }

    return {
      id: admin.id,
      name: admin.full_name || `${admin.firstname || ''} ${admin.lastname || ''}`.trim() || 'Unknown',
      age: calculateAge(admin.date_of_birth),
      facility: resolveFacilityName(admin),
      lga: resolveLgaName(admin),
      jobRole,
      role: roleType === 'super_admin' ? 'Super Admin' : 'Admin',
      email: admin.email || 'N/A',
      phone: admin.phone_number || admin.phone || 'N/A',
      image: course1,
      status: status,
      createdAtRaw,
      createdAt: createdAtRaw ? new Date(createdAtRaw).toLocaleDateString() : 'N/A',
      lastActiveRaw,
      lastActive: lastActiveRaw ? new Date(lastActiveRaw).toLocaleString() : 'Recently active',
    };
  };

  const getInitials = (name = '') => {
    if (!name) return 'AD';
    const initials = name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('');
    return initials || 'AD';
  };

  const updateAdminStats = (list = []) => {
    if (!Array.isArray(list)) {
      setAdminStats({
        total: 0,
        active: 0,
        superAdmins: 0,
        reachable: 0,
        recent: null,
      });
      return;
    }

    const total = list.length;
    const active = list.filter((admin) => {
      const status = (admin.status || '').toLowerCase();
      return status === 'active';
    }).length;
    const superAdmins = list.filter((admin) => admin.role?.toLowerCase().includes('super')).length;
    const reachable = list.filter((admin) => admin.phone && admin.phone !== 'N/A').length;
    const recent = [...list]
      .filter((admin) => admin.createdAtRaw)
      .sort((a, b) => new Date(b.createdAtRaw) - new Date(a.createdAtRaw))[0] || null;

    setAdminStats({
      total,
      active,
      superAdmins,
      reachable,
      recent,
    });
  };

  const fetchLearnersData = useCallback(async (options = {}) => {
    const filters = {
      from: options.from ?? hwDateFrom,
      to: options.to ?? hwDateTo,
      page: options.page ?? 1,
      per_page: 50,
      search: options.search ?? searchTerm,
    };
    try {
      const result = await getLearners(filters);
      const mappedLearners = result.records.map(formatHealthWorker);
      setAllHealthWorkers(mappedLearners);
      setHwPagination(result.pagination);
      return mappedLearners;
    } catch (err) {
      console.error('Error fetching learners:', err);
      setAllHealthWorkers([]);
      setHwPagination({ currentPage: 1, lastPage: 1, perPage: 50, total: 0 });
      throw err;
    }
  }, [hwDateFrom, hwDateTo, searchTerm]);

  const fetchAdminsData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    const filters = {
      from: options.from ?? adminDateFrom,
      to: options.to ?? adminDateTo,
      search: options.search ?? searchTerm,
    };
    if (!silent) {
      setRefreshingAdmins(true);
    }

    try {
      const adminsData = await getAdmins(filters);
      const adminsMapped = adminsData.map(formatAdmin);
      setAllAdmins(adminsMapped);
      updateAdminStats(adminsMapped);
      setAdminDataLoaded(true);
      return adminsMapped;
    } catch (err) {
      console.error('Error fetching admins:', err);
      setAllAdmins([]);
      updateAdminStats([]);
      setAdminDataLoaded(false);
      throw err;
    } finally {
      if (!silent) {
        setRefreshingAdmins(false);
      }
    }
  }, [adminDateFrom, adminDateTo, searchTerm]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        await fetchLearnersData();
      } catch (error) {
        console.error('Failed to process data:', error);
        if (isMounted) {
          setError('Failed to load data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
    // Runs once on mount only — fetchLearnersData/fetchAdminsData change identity
    // whenever search/date filters change, and those are handled by dedicated
    // effects below instead of re-triggering this initial full-page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'admin' && !adminDataLoaded && !refreshingAdmins) {
      fetchAdminsData().catch((err) => {
        console.error('Failed to load admins when tab activated:', err);
      });
    }
  }, [activeTab, adminDataLoaded, refreshingAdmins, fetchAdminsData]);

  // Debounced backend search: re-query the active tab's listing endpoint
  // instead of filtering only the currently-loaded page client-side.
  // Fetch functions are read via refs (always the latest version) so this
  // effect only re-fires on search/tab changes, not on unrelated date-filter edits.
  const fetchLearnersRef = useRef(fetchLearnersData);
  fetchLearnersRef.current = fetchLearnersData;
  const fetchAdminsRef = useRef(fetchAdminsData);
  fetchAdminsRef.current = fetchAdminsData;

  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }

    const handle = setTimeout(() => {
      if (activeTab === 'health-workers') {
        fetchLearnersRef.current({ page: 1 }).catch((err) => {
          console.error('Health worker search failed:', err);
        });
      } else if (activeTab === 'admin' && adminDataLoaded) {
        fetchAdminsRef.current({ silent: true }).catch((err) => {
          console.error('Admin search failed:', err);
        });
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [searchTerm, activeTab, adminDataLoaded]);


  const handleViewDetails = (userId) => {
    if (activeTab === 'health-workers') {
      navigate(`/learners/${userId}`);
    } else {
      navigate(`/admins/${userId}`);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedUsers([]);
    setSearchTerm('');
    setSuccessMessage('');
    if (tab !== 'admin') {
      setAdminViewMode('table');
    }
    setAdminRoleFilter('all');
  };

  const handleCreateHealthWorker = async (result = {}) => {
    const workerName =
      result?.full_name ||
      `${result?.firstname || ''} ${result?.lastname || ''}`.trim() ||
      'Health worker';
    setSuccessMessage(`${workerName} created successfully.`);
    try {
      await fetchLearnersData();
      setActiveTab('healthWorker');
    } catch (err) {
      console.error('Failed to refresh health workers after creation:', err);
    }
  };

  const handleAdminCreated = async (newAdmin = {}) => {
    const adminName =
      newAdmin?.full_name ||
      `${newAdmin?.firstname || ''} ${newAdmin?.lastname || ''}`.trim() ||
      'New admin';
    setSuccessMessage(`${adminName} added successfully.`);
    try {
      await fetchAdminsData();
      setActiveTab('admin');
    } catch (err) {
      console.error('Failed to refresh admins after creation:', err);
    }
  };

  const handleAdminViewChange = (mode) => {
    setAdminViewMode(mode);
    setSelectedUsers([]);
  };

  const handleAdminFilterChange = (value) => {
    setAdminRoleFilter(value);
    setAdminCurrentPage(1);
  };

  const handleRefreshAdmins = async () => {
    try {
      await fetchAdminsData();
      setSuccessMessage('Admin data refreshed.');
    } catch (err) {
      console.error('Failed to refresh admins on demand:', err);
      setSuccessMessage('Unable to refresh admins right now. Please try again.');
    }
  };

  const handleApplyHWDateFilter = async () => {
    if (hwDateFrom && hwDateTo && hwDateFrom > hwDateTo) {
      setSuccessMessage('Invalid date range: From date cannot be after To date.');
      return;
    }
    try {
      await fetchLearnersData({ from: hwDateFrom, to: hwDateTo });
      setSuccessMessage('Health worker date filter applied.');
    } catch (err) {
      console.error('Failed to apply health worker date filter:', err);
      setSuccessMessage('Unable to apply date filter right now. Please try again.');
    }
  };

  const handleClearHWDateFilter = async () => {
    setHwDateFrom('');
    setHwDateTo('');
    try {
      await fetchLearnersData({ from: '', to: '' });
      setSuccessMessage('Health worker date filter cleared.');
    } catch (err) {
      console.error('Failed to clear health worker date filter:', err);
      setSuccessMessage('Unable to clear date filter right now. Please try again.');
    }
  };

  const handleApplyAdminDateFilter = async () => {
    if (adminDateFrom && adminDateTo && adminDateFrom > adminDateTo) {
      setSuccessMessage('Invalid date range: From date cannot be after To date.');
      return;
    }

    try {
      await fetchAdminsData({ from: adminDateFrom, to: adminDateTo });
      setSuccessMessage('Admin date filter applied.');
      setAdminCurrentPage(1);
    } catch (err) {
      console.error('Failed to apply admin date filter:', err);
      setSuccessMessage('Unable to apply date filter right now. Please try again.');
    }
  };

  const handleClearAdminDateFilter = async () => {
    setAdminDateFrom('');
    setAdminDateTo('');
    try {
      await fetchAdminsData({ from: '', to: '' });
      setSuccessMessage('Admin date filter cleared.');
      setAdminCurrentPage(1);
    } catch (err) {
      console.error('Failed to clear admin date filter:', err);
      setSuccessMessage('Unable to clear date filter right now. Please try again.');
    }
  };


  const copyToClipboard = (value) => {
    if (value && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => { });
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const currentUsers = filteredUsers;
    if (selectedUsers.length === currentUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers.map(user => user.id));
    }
  };

  const handleSuspendUser = async () => {
    if (selectedUsers.length === 0) return;

    const usersToSuspend = (activeTab === 'health-workers' ? allHealthWorkers : allAdmins)
      .filter(user => selectedUsers.includes(user.id));

    const isSuspending = usersToSuspend.some(user => {
      const status = (user.status || '').toLowerCase();
      return status === 'active';
    });
    const action = isSuspending ? 'suspend' : 'activate';
    const userType = activeTab === 'health-workers' ? 'health workers' : 'admins';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedUsers.length > 1 ? `these ${userType}` : `this ${userType.slice(0, -1)}`}?`)) return;

    setSuspending(true);
    setSuccessMessage('');
    setError(null);
    try {
      if (activeTab === 'admin') {
        // For admins, call the actual API
        const suspendPromises = selectedUsers.map(async (userId) => {
          const user = allAdmins.find(u => u.id === userId);
          const userStatus = (user?.status || '').toLowerCase();
          const shouldSuspend = userStatus === 'active';
          return await suspendAdmin(userId, shouldSuspend);
        });

        await Promise.all(suspendPromises);

        // Refresh admin list to get updated status
        await fetchAdminsData();

        setSuccessMessage(`Successfully ${isSuspending ? 'suspended' : 'activated'} ${selectedUsers.length > 1 ? 'admins' : 'admin'}.`);
      } else {
        // For health workers, call the actual API
        const suspendPromises = selectedUsers.map(async (userId) => {
          const user = allHealthWorkers.find(u => u.id === userId);
          const userStatus = (user?.status || '').toLowerCase();
          const shouldSuspend = userStatus === 'active';
          return await suspendLearner(userId, shouldSuspend);
        });

        await Promise.all(suspendPromises);

        // Refresh health workers list
        await fetchLearnersData();

        setSuccessMessage(`Successfully ${isSuspending ? 'suspended' : 'activated'} ${selectedUsers.length > 1 ? 'users' : 'user'}.`);
      }

      setSelectedUsers([]);
    } catch (error) {
      console.error('Failed to suspend/activate user:', error);
      setError(error?.message || `Failed to ${isSuspending ? 'suspend' : 'activate'} ${activeTab === 'health-workers' ? 'user(s)' : 'admin(s)'}. Please try again.`);
    } finally {
      setSuspending(false);
    }
  };

  const handleIndividualSuspendActivate = async (userId, currentStatus) => {
    const user = activeTab === 'health-workers'
      ? allHealthWorkers.find(u => u.id === userId)
      : allAdmins.find(u => u.id === userId);

    if (!user) return;

    const userStatus = (user.status || '').toLowerCase();
    const shouldSuspend = userStatus === 'active';
    const action = shouldSuspend ? 'suspend' : 'activate';
    const userType = activeTab === 'health-workers' ? 'health worker' : 'admin';
    if (!window.confirm(`Are you sure you want to ${action} ${user.name}?`)) return;

    setActivating({ userId, active: !shouldSuspend });
    setSuccessMessage('');
    setError(null);
    try {
      if (activeTab === 'admin') {
        await suspendAdmin(userId, shouldSuspend);
        await fetchAdminsData();
        setSuccessMessage(`Successfully ${action}ed ${user.name}.`);
      } else {
        await suspendLearner(userId, shouldSuspend);
        await fetchLearnersData();
        setSuccessMessage(`Successfully ${action}ed ${user.name}.`);
      }
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      setError(error?.message || `Failed to ${action} ${userType}. Please try again.`);
    } finally {
      setActivating({ userId: null, active: false });
    }
  };

  const handleDeleteUser = (userId, userName) => {
    const userType = activeTab === 'health-workers' ? 'health worker' : 'admin';
    setDeleteConfirm({
      open: true,
      userId,
      userName,
      userType
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.userId) return;

    setDeleting(true);
    setSuccessMessage('');
    setError(null);
    try {
      if (activeTab === 'admin') {
        await deleteAdmin(deleteConfirm.userId);
        await fetchAdminsData();
        setSuccessMessage(`Successfully deleted ${deleteConfirm.userName}.`);
      } else {
        await deleteLearner(deleteConfirm.userId);
        await fetchLearnersData();
        setSuccessMessage(`Successfully deleted ${deleteConfirm.userName}.`);
      }
      setDeleteConfirm({ open: false, userId: null, userName: '', userType: '' });
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(error?.message || `Failed to delete ${deleteConfirm.userType}. Please try again.`);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ open: false, userId: null, userName: '', userType: '' });
  };

  const handleBulkUpload = (users) => {
    const newUsers = users.map((user, index) => ({
      id: (activeTab === 'health-workers' ? allHealthWorkers : allAdmins).length + index + 1,
      name: user.name || 'Unknown',
      age: user.age || 'N/A',
      facility: user.facility || 'N/A',
      lga: user.lga || 'N/A',
      jobRole: user.jobRole || 'N/A',
      image: course1,
      status: 'Active',
    }));
    if (activeTab === 'health-workers') {
      setAllHealthWorkers(prev => [...prev, ...newUsers]);
    } else {
      setAllAdmins(prev => [...prev, ...newUsers]);
    }
    setSuccessMessage(`Successfully uploaded ${newUsers.length} ${activeTab === 'health-workers' ? 'health workers' : 'admins'}.`);
  };

  const handleExport = (format, selectedColumns) => {
    const usersToExport = selectedUsers.length > 0
      ? filteredUsers.filter(user => selectedUsers.includes(user.id))
      : filteredUsers;

    if (usersToExport.length === 0) {
      setSuccessMessage('No users to export.');
      return;
    }

    let content;
    let mimeType;
    const fileName = `${activeTab === 'health-workers' ? 'health-workers' : 'admins'}-export-${new Date().toISOString().split('T')[0]}.${format}`;

    if (format === 'csv') {
      const headers = selectedColumns.map(col => col.label).join(',');
      const rows = usersToExport.map(user =>
        selectedColumns.map(col => `"${(user[col.key] || '').toString().replace(/"/g, '""')}"`).join(',')
      );
      content = [headers, ...rows].join('\n');
      mimeType = 'text/csv';
    } else if (format === 'json') {
      const filteredData = usersToExport.map(user => {
        const obj = {};
        selectedColumns.forEach(col => {
          obj[col.key] = user[col.key];
        });
        return obj;
      });
      content = JSON.stringify(filteredData, null, 2);
      mimeType = 'application/json';
    } else if (format === 'pdf') {
      // Mock PDF content (requires jsPDF for real implementation)
      content = 'PDF export not fully implemented. Use jsPDF for actual PDF generation.';
      mimeType = 'text/plain'; // Fallback for mock
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);

    setSuccessMessage(`Successfully exported ${usersToExport.length} ${activeTab === 'health-workers' ? 'health workers' : 'admins'} in ${format.toUpperCase()} format.`);
  };

  const sortedUsers = useMemo(() => {
    const users = activeTab === 'health-workers' ? allHealthWorkers : allAdmins;
    return [...users].sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'age') {
        const aAge = a.age === 'N/A' ? Infinity : parseInt(a.age, 10);
        const bAge = b.age === 'N/A' ? Infinity : parseInt(b.age, 10);
        return aAge - bAge;
      }
      return 0;
    });
  }, [activeTab, allAdmins, allHealthWorkers, sortBy]);

  const filteredUsers = useMemo(() => {
    // Search itself is handled server-side (name/email/phone) via the debounced
    // fetch above — sortedUsers already reflects the current search term once
    // that request resolves, so this only applies the admin role filter.
    let users = sortedUsers;

    if (activeTab === 'admin' && adminRoleFilter !== 'all') {
      users = users.filter((user) => {
        const role = (user.role || '').toLowerCase();
        return adminRoleFilter === 'super_admin' ? role.includes('super') : !role.includes('super');
      });
    }

    return users;
  }, [activeTab, adminRoleFilter, sortedUsers]);

  const shouldShowCards = activeTab === 'admin' && adminViewMode === 'cards';
  const adminTabSyncing = activeTab === 'admin' && (!adminDataLoaded || refreshingAdmins);

  // Admin frontend pagination (admins are fetched all at once)
  const adminIndexOfLast = adminCurrentPage * adminUsersPerPage;
  const adminIndexOfFirst = adminIndexOfLast - adminUsersPerPage;
  const adminCurrentUsers = filteredUsers.slice(adminIndexOfFirst, adminIndexOfLast);
  const adminTotalPages = Math.ceil(filteredUsers.length / adminUsersPerPage);

  // Reset admin page on filter/search changes
  useEffect(() => {
    setAdminCurrentPage(1);
  }, [searchTerm, sortBy, activeTab, adminRoleFilter]);

  const getActionButtons = () => {
    if (activeTab === 'health-workers') {
      return (
        <>
          <button
            className="btn-suspend-user"
            disabled={suspending || selectedUsers.length === 0}
            onClick={handleSuspendUser}
            aria-label="Suspend selected health workers"
          >
            {suspending ? (
              <>
                <span className="spinner"></span> Suspending...
              </>
            ) : (
              `Suspend User${selectedUsers.length > 1 ? 's' : ''}`
            )}
          </button>
          <button
            className="btn-bulk-upload"
            onClick={() => {
              setBulkUploadType('health-workers');
              setShowBulkUploadModal(true);
            }}
            aria-label="Bulk upload health workers"
            title="Upload multiple health workers via CSV"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="upload-icon">
              <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z" />
            </svg>
            Bulk Upload Users
          </button>
          <button
            className="btn-add-user"
            onClick={() => setShowCreateHealthWorkerModal(true)}
            aria-label="Add new health worker"
          >
            Add User
          </button>
        </>
      );
    } else {
      return (
        <>
          <button
            className="btn-suspend-user"
            disabled={suspending || selectedUsers.length === 0}
            onClick={handleSuspendUser}
            aria-label="Suspend selected admins"
          >
            {suspending ? (
              <>
                <span className="spinner"></span> Suspending...
              </>
            ) : (
              `Suspend Admin${selectedUsers.length > 1 ? 's' : ''}`
            )}
          </button>
          <button
            className="btn-bulk-upload"
            onClick={() => {
              setBulkUploadType('admin');
              setShowBulkUploadModal(true);
            }}
            aria-label="Bulk upload admins"
            title="Upload multiple admins via CSV"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="upload-icon">
              <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z" />
            </svg>
            Bulk Upload Admins
          </button>
          <button
            className="btn-add-user"
            onClick={() => setShowCreateAdminModal(true)}
            aria-label="Add new admin"
          >
            Add Admin
          </button>
        </>
      );
    }
  };

  const getSearchPlaceholder = () => {
    return activeTab === 'health-workers' ? 'Search health workers by name' : 'Search admins by name';
  };

  const getTableHeaders = () => (
    <>
      <th className="select-cell">
        <input
          type="checkbox"
          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
          onChange={handleSelectAll}
          aria-label="Select all users"
        />
      </th>
      <th>Name</th>
      <th>Age</th>
      {activeTab === 'health-workers' && (
        <>
          <th>Facility</th>
          <th>LGA</th>
        </>
      )}
      {activeTab === 'admin' && (
        <>
          <th>Role</th>
          <th>Contact / Location</th>
        </>
      )}
      {activeTab === 'health-workers' && <th>Job Role</th>}
      <th>Status</th>
    </>
  );

  const getTableRow = (user) => (
    <>
      <td className="select-cell" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedUsers.includes(user.id)}
          onChange={() => handleSelectUser(user.id)}
          aria-label={`Select ${user.name}`}
        />
      </td>
      <td className="name-cell">
        <img src={user.image} alt={user.name} className="user-avatar" />
        <div className="name-info">
          <span className="user-name">{user.name}</span>
          {user.email && (
            <span className="user-email-small">{user.email}</span>
          )}
        </div>
      </td>
      <td className="age-cell">
        <span className="age-badge">{user.age}</span>
      </td>
      {activeTab === 'health-workers' && (
        <>
          <td className="facility-cell">
            <div className="location-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="location-icon">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>{user.facility}</span>
            </div>
          </td>
          <td className="lga-cell">
            <div className="location-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="location-icon">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>{user.lga}</span>
            </div>
          </td>
        </>
      )}
      {activeTab === 'admin' && (
        <>
          <td className="role-cell">
            <span className={`role-badge ${user.role?.toLowerCase().replace(' ', '-') || 'admin'}`}>
              {user.role || 'Admin'}
            </span>
          </td>
          <td className="contact-cell">
            <div className="contact-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79a15.09 15.09 0 006.59 6.59l2.2-2.2a1 1 0 011-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1 17 17 0 01-17-17 1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.24 1z" />
              </svg>
              <span>{user.phone || 'N/A'}</span>
            </div>
            <div className="contact-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12.713l11.985-8.713H.015L12 12.713zm0 2.574L0 6v12h24V6L12 15.287z" />
              </svg>
              <span>{user.email || 'N/A'}</span>
            </div>
            <div className="contact-pill muted">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              </svg>
              <span>{user.facility}{user.lga !== 'N/A' ? ` • ${user.lga}` : ''}</span>
            </div>
          </td>
        </>
      )}
      {activeTab === 'health-workers' && (
        <td className="job-role-cell">
          <span className="job-role-badge">{user.jobRole}</span>
        </td>
      )}
      <td className="status-cell">
        <span className={`status-badge ${user.status.toLowerCase().includes('suspended') || user.status.toLowerCase().includes('inactive') ? 'suspended' : user.status.toLowerCase()}`}>
          {user.status}
        </span>
      </td>
    </>
  );

  const renderAdminCards = () => (
    <div className="admin-card-grid">
      {filteredUsers.map((admin) => (
        <div key={admin.id} className="admin-card">
          <div className="admin-card-header">
            <div className="admin-avatar-circle">{getInitials(admin.name)}</div>
            <div className="admin-card-title">
              <h3>{admin.name}</h3>
              <span className="admin-role-chip">{admin.role}</span>
            </div>
            <span className={`status-pill ${admin.status.toLowerCase().includes('suspended') || admin.status.toLowerCase().includes('inactive') ? 'suspended' : admin.status.toLowerCase()}`}>{admin.status}</span>
          </div>
          <div className="admin-card-body">
            <div className="admin-card-meta">
              <span>{admin.jobRole}</span>
              <span>{admin.facility}</span>
            </div>
            <div className="admin-card-stats">
              <div>
                <label>Contact</label>
                <p>{admin.phone}</p>
              </div>
              <div>
                <label>Email</label>
                <p>{admin.email}</p>
              </div>
              <div>
                <label>LGA</label>
                <p>{admin.lga}</p>
              </div>
            </div>
            <div className="admin-card-timestamps">
              <span>Joined: {admin.createdAt}</span>
              <span>Last active: {admin.lastActive}</span>
            </div>
          </div>
          <div className="admin-card-actions">
            <button
              type="button"
              className="admin-card-btn subtle"
              onClick={() => handleViewDetails(admin.id)}
            >
              View profile
            </button>
            <button
              type="button"
              className="admin-card-btn subtle"
              onClick={() => copyToClipboard(admin.email || '')}
            >
              Copy email
            </button>
            <div className="admin-card-quick-actions">
              <button
                type="button"
                className={`admin-card-icon-btn ${(admin.status || '').toLowerCase() === 'active' ? 'suspend' : 'activate'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleIndividualSuspendActivate(admin.id, admin.status);
                }}
                disabled={activating.userId === admin.id}
                title={(admin.status || '').toLowerCase() === 'active' ? 'Suspend' : 'Unsuspend'}
              >
                {activating.userId === admin.id ? (
                  <span className="mini-spinner"></span>
                ) : (admin.status || '').toLowerCase() === 'active' ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                    <span className="btn-text">Suspend</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span className="btn-text">Unsuspend</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className="admin-card-icon-btn delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteUser(admin.id, admin.name);
                }}
                disabled={deleting}
                title="Delete"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        Loading {activeTab === 'health-workers' ? 'health workers' : 'admins'}...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
        Error: {error}
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="user-management-header">
        <h1>User Management</h1>
      </div>

      <div className="user-management-tabs">
        <button
          className={`tab-button ${activeTab === 'health-workers' ? 'active' : ''}`}
          onClick={() => handleTabChange('health-workers')}
          aria-label="View health workers"
        >
          Health Workers <span className="tab-count">({hwPagination.total > 0 ? hwPagination.total : allHealthWorkers.length})</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={() => handleTabChange('admin')}
          aria-label="View admins"
        >
          Admins <span className="tab-count">({allAdmins.length})</span>
        </button>
      </div>

      <div className="user-management-content">
        {successMessage && (
          <div className="success-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="success-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            {successMessage}
          </div>
        )}
        <div className="action-buttons">
          {getActionButtons()}
        </div>

        {activeTab === 'health-workers' && (
          <div className="admin-toolbar">
            <div className="admin-date-filter-group">
              <label>
                From
                <input
                  type="date"
                  value={hwDateFrom}
                  onChange={(e) => setHwDateFrom(e.target.value)}
                  className="admin-date-input"
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={hwDateTo}
                  onChange={(e) => setHwDateTo(e.target.value)}
                  className="admin-date-input"
                />
              </label>
              <button
                type="button"
                className="admin-date-btn apply"
                onClick={handleApplyHWDateFilter}
              >
                Apply
              </button>
              <button
                type="button"
                className="admin-date-btn clear"
                onClick={handleClearHWDateFilter}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {activeTab === 'admin' && (
          <>
            <div className="admin-hero-panel">
              <div className="admin-hero-copy">
                <p className="admin-eyebrow">Admin oversight</p>
                <h2>Admin Command Center</h2>
                <p>
                  Keep tabs on leadership coverage, communication readiness, and the most recent
                  onboarded admins at a glance.
                </p>
                {adminStats.recent && (
                  <div className="admin-recent-chip">
                    <span className="chip-dot"></span>
                    Last onboarded: <strong>{adminStats.recent.name}</strong> on{' '}
                    {adminStats.recent.createdAt}
                  </div>
                )}
              </div>
              <div className="admin-hero-stats">
                <div className="admin-hero-stat">
                  <span>Total Admins</span>
                  <strong>{adminStats.total}</strong>
                  <small>{adminStats.active} active right now</small>
                </div>
                <div className="admin-hero-stat">
                  <span>Super Admins</span>
                  <strong>{adminStats.superAdmins}</strong>
                  <small>Leadership coverage</small>
                </div>
                <div className="admin-hero-stat">
                  <span>Reachable</span>
                  <strong>{adminStats.reachable}</strong>
                  <small>Phone on file</small>
                </div>
              </div>
            </div>
            <div className="admin-toolbar">
              <div className="admin-role-filter">
                {ADMIN_FILTER_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={`filter-chip ${adminRoleFilter === option.id ? 'active' : ''}`}
                    onClick={() => handleAdminFilterChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="admin-toolbar-actions">
                <div className="admin-date-filter-group">
                  <label>
                    From
                    <input
                      type="date"
                      value={adminDateFrom}
                      onChange={(e) => setAdminDateFrom(e.target.value)}
                      className="admin-date-input"
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="date"
                      value={adminDateTo}
                      onChange={(e) => setAdminDateTo(e.target.value)}
                      className="admin-date-input"
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-date-btn apply"
                    onClick={handleApplyAdminDateFilter}
                    disabled={refreshingAdmins}
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    className="admin-date-btn clear"
                    onClick={handleClearAdminDateFilter}
                    disabled={refreshingAdmins}
                  >
                    Clear
                  </button>
                </div>
                <div className="admin-view-toggle">
                  <button
                    type="button"
                    className={`view-toggle-btn ${adminViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => handleAdminViewChange('table')}
                  >
                    Table
                  </button>
                  <button
                    type="button"
                    className={`view-toggle-btn ${adminViewMode === 'cards' ? 'active' : ''}`}
                    onClick={() => handleAdminViewChange('cards')}
                  >
                    Cards
                  </button>
                </div>
                <button
                  type="button"
                  className="admin-refresh-btn"
                  onClick={handleRefreshAdmins}
                  disabled={refreshingAdmins}
                >
                  {refreshingAdmins ? (
                    <>
                      <span className="mini-spinner"></span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.65 6.35a7.95 7.95 0 00-11.3 0L4 8V2h6L7.35 4.65a5.96 5.96 0 018.45 0 5.96 5.96 0 010 8.45l-1.2 1.2-1.42-1.42 1.2-1.2a3.97 3.97 0 000-5.61zM20 16v6h-6l2.65-2.65a5.96 5.96 0 01-8.45 0 5.96 5.96 0 010-8.45l1.2-1.2 1.42 1.42-1.2 1.2a3.97 3.97 0 000 5.61 3.97 3.97 0 005.61 0L16 16h4z" />
                      </svg>
                      Sync data
                    </>
                  )}
                </button>
              </div>
            </div>
            {adminTabSyncing && (
              <div className="inline-loading-banner">
                <span className="mini-spinner"></span>
                Syncing admin directory...
              </div>
            )}
          </>
        )}

        <div className="search-filter-bar">
          <div className="search-section">
            <input
              type="text"
              placeholder={getSearchPlaceholder()}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              aria-label={getSearchPlaceholder()}
            />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="search-icon">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            )}
          </div>
          <div className="filter-section">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              aria-label="Sort users by"
            >
              <option value="name">Sort by Name</option>
              <option value="age">Sort by Age</option>
            </select>
          </div>
        </div>

        <div className="users-table">
          {filteredUsers.length === 0 ? (
            <div className="empty-state">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="empty-icon">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
              </svg>
              No {activeTab === 'health-workers' ? 'health workers' : 'admins'} found. Try adjusting your search or add a new {activeTab === 'health-workers' ? 'user' : 'admin'}.
            </div>
          ) : shouldShowCards ? (
            renderAdminCards()
          ) : (
            <>
              <table>
                <thead>
                  <tr>{getTableHeaders()}</tr>
                </thead>
                <tbody>
                  {(activeTab === 'health-workers' ? filteredUsers : adminCurrentUsers).map((user) => (
                    <tr
                      key={user.id}
                      className={`user-row ${selectedUsers.includes(user.id) ? 'selected-row' : ''}`}
                      onClick={() => handleViewDetails(user.id)}
                    >
                      {getTableRow(user)}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Health workers: backend pagination */}
              {activeTab === 'health-workers' && hwPagination.lastPage > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Page {hwPagination.currentPage} of {hwPagination.lastPage} &mdash; {hwPagination.total} health workers total
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => fetchLearnersData({ page: hwPagination.currentPage - 1 })}
                      disabled={hwPagination.currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-pages">
                      {Array.from({ length: hwPagination.lastPage }, (_, i) => i + 1)
                        .filter(page =>
                          page === 1 ||
                          page === hwPagination.lastPage ||
                          (page >= hwPagination.currentPage - 2 && page <= hwPagination.currentPage + 2)
                        )
                        .map((page, index, array) => {
                          const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsisBefore && <span className="pagination-ellipsis">...</span>}
                              <button
                                className={`pagination-page-btn ${hwPagination.currentPage === page ? 'active' : ''}`}
                                onClick={() => fetchLearnersData({ page })}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => fetchLearnersData({ page: hwPagination.currentPage + 1 })}
                      disabled={hwPagination.currentPage === hwPagination.lastPage}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Admins: frontend pagination */}
              {activeTab === 'admin' && adminTotalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {adminIndexOfFirst + 1} to {Math.min(adminIndexOfLast, filteredUsers.length)} of {filteredUsers.length} admins
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => setAdminCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={adminCurrentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-pages">
                      {Array.from({ length: adminTotalPages }, (_, i) => i + 1)
                        .filter(page =>
                          page === 1 ||
                          page === adminTotalPages ||
                          (page >= adminCurrentPage - 2 && page <= adminCurrentPage + 2)
                        )
                        .map((page, index, array) => {
                          const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsisBefore && <span className="pagination-ellipsis">...</span>}
                              <button
                                className={`pagination-page-btn ${adminCurrentPage === page ? 'active' : ''}`}
                                onClick={() => setAdminCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => setAdminCurrentPage(prev => Math.min(adminTotalPages, prev + 1))}
                      disabled={adminCurrentPage === adminTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CreateHealthWorkerModal
        isOpen={showCreateHealthWorkerModal}
        onClose={() => setShowCreateHealthWorkerModal(false)}
        onSubmit={handleCreateHealthWorker}
      />

      <CreateAdminModal
        isOpen={showCreateAdminModal}
        onClose={() => setShowCreateAdminModal(false)}
        onSubmit={handleAdminCreated}
      />

      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUpload}
        type={bulkUploadType}
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        context={{ type: 'users', filteredUsers, activeTab, searchTerm, sortBy }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.open && (
        <div className="delete-confirm-overlay" onClick={cancelDelete}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <div className="delete-confirm-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h3>Confirm Deletion</h3>
            </div>
            <div className="delete-confirm-body">
              <p>
                Are you sure you want to delete <strong>{deleteConfirm.userName}</strong>?
              </p>
              <p className="delete-warning">
                This action cannot be undone. All data associated with this {deleteConfirm.userType} will be permanently removed.
              </p>
            </div>
            <div className="delete-confirm-actions">
              <button
                className="btn-cancel"
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete-confirm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="mini-spinner"></span>
                    Deleting...
                  </>
                ) : (
                  'Delete Permanently'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;